import i18n, { ensureLocaleLoaded, resolveEwDisplayLocaleCode } from "../i18n";
import UserAPIs from "../apis/user.apis";
import {
    copyCache,
    overlayStockCopy,
    rememberStock,
    type WidgetFormCopyKind,
} from "./widgetFormCopy";

function cacheKey(kind: WidgetFormCopyKind, locale: string): string {
    return `${kind}:${locale}`;
}

function readReviewCopy(locale: string): Record<string, string> {
    const t = i18n.getFixedT(locale, "reviews");
    return {
        review_button_text: t("widgetSettings.placeholderButtonText"),
        first_review_text: t("widgetSettings.placeholderFirstReview"),
        based_on_reviews_text: t("widgetSettings.placeholderBasedOn"),
        popup_title_text: t("widgetSettings.placeholderPopupTitle"),
        popup_rating_excellent_text: t("widgetSettings.placeholderExcellent"),
        popup_rating_just_wow_text: t("widgetSettings.placeholderJustWow"),
        popup_rating_i_like_it_text: t("widgetSettings.placeholderILikeIt"),
        popup_rating_i_dont_like_it_text: t("widgetSettings.placeholderIDontLikeIt"),
        popup_rating_i_hate_it_text: t("widgetSettings.placeholderIHateIt"),
        review_media_gallery_title: t("widgetSettings.storefrontCopy.mediaGalleryTitle"),
        review_media_view_all_text: t("widgetSettings.storefrontCopy.mediaViewAll"),
        review_trust_1_title: t("widgetSettings.storefrontCopy.trust1Title"),
        review_trust_1_subtitle: t("widgetSettings.storefrontCopy.trust1Subtitle"),
        review_trust_2_title: t("widgetSettings.storefrontCopy.trust2Title"),
        review_trust_2_subtitle: t("widgetSettings.storefrontCopy.trust2Subtitle"),
        review_trust_3_title: t("widgetSettings.storefrontCopy.trust3Title"),
        review_trust_3_subtitle: t("widgetSettings.storefrontCopy.trust3Subtitle"),
        review_cta_title: t("widgetSettings.storefrontCopy.ctaTitle"),
        review_cta_subtitle: t("widgetSettings.storefrontCopy.ctaSubtitle"),
        review_list_section_title: t("widgetSettings.storefrontCopy.listSectionTitle"),
        review_verified_buyer_text: t("widgetSettings.storefrontCopy.verifiedBuyer"),
        review_reply_from_text: t("widgetSettings.storefrontCopy.replyFrom"),
        review_store_badge_text: t("widgetSettings.storefrontCopy.storeBadge"),
    };
}

function readChatCopy(locale: string): Record<string, string> {
    const t = i18n.getFixedT(locale, "chats");
    return {
        welcome_message: t("settings.defaults.welcomeMessage"),
        info_message: t("settings.defaults.infoMessage"),
        offline_message: t("settings.defaults.offlineMessage"),
        ai_handoff_message: t("settings.defaults.aiHandoffMessage"),
        engage_button_label: t("settings.defaults.engageButtonLabel"),
    };
}

function putCopy(kind: WidgetFormCopyKind, locale: string, copy: Record<string, string>): void {
    if (!copy || Object.keys(copy).length === 0) {
        return;
    }
    copyCache.set(cacheKey(kind, locale), copy);
    rememberStock(copy);
}

export function seedWidgetFormCopy(
    locale: string,
    maps: { reviews?: Record<string, string>; chats?: Record<string, string> }
): void {
    const resolved = resolveEwDisplayLocaleCode(locale) || locale || "en";
    if (maps.reviews) {
        putCopy("reviews", resolved, maps.reviews);
    }
    if (maps.chats) {
        putCopy("chats", resolved, maps.chats);
    }
}

async function fetchRemoteCopy(locale: string): Promise<void> {
    const remote = await new UserAPIs().load_widget_copy(locale);
    if (UserAPIs.hasErrorNotification(remote)) {
        return;
    }
    seedWidgetFormCopy(locale, {
        reviews: remote.reviews,
        chats: remote.chats,
    });
}

export async function loadWidgetFormCopy(
    kind: WidgetFormCopyKind,
    locale: string
): Promise<Record<string, string>> {
    const resolved = resolveEwDisplayLocaleCode(locale) || locale || "en";
    const key = cacheKey(kind, resolved);
    const cached = copyCache.get(key);
    if (cached) {
        return cached;
    }
    try {
        await fetchRemoteCopy(resolved);
    } catch {
        /* fall through to i18n pack */
    }
    const fromApi = copyCache.get(key);
    if (fromApi) {
        return fromApi;
    }
    await ensureLocaleLoaded(resolved);
    const copy = kind === "reviews" ? readReviewCopy(resolved) : readChatCopy(resolved);
    putCopy(kind, resolved, copy);
    return copy;
}

export async function applyWidgetFormLocale<T extends Record<string, unknown>>(
    kind: WidgetFormCopyKind,
    values: T,
    fromLocale: string,
    toLocale: string
): Promise<T> {
    const [prevCopy, nextCopy] = await Promise.all([
        loadWidgetFormCopy(kind, fromLocale),
        loadWidgetFormCopy(kind, toLocale),
    ]);
    return overlayStockCopy(values, prevCopy, nextCopy);
}
